/**
 * Which brand a program or enrollment belongs to, as a small pill.
 *
 * Needed wherever a row can be read outside its own catalog: the student profile lists a
 * family's enrollments across both brands in one list, and a program or enrollment detail page
 * can be reached by a link from anywhere. In those places "Clay & Canvas" alone does not say
 * whose class it is.
 *
 * Renders nothing for an unrecognized brand rather than guessing — a row selected without
 * `brand_key` (an older query, a fixture) should stay as it was rather than be labelled BHCS
 * on this component's say-so. That is a display decision only; `brandProfileOf` still resolves
 * a missing brand to the default everywhere a *behaviour* depends on it.
 */

import { isBrandKey } from '../config/brands';
import { useBrandProfile } from '../contexts/brand-profile';
import { BRAND_PROFILES } from '../config/brandProfiles';

interface BrandBadgeProps {
    /** Raw `programs.brand_key`. Anything unrecognized renders nothing. */
    brand: string | null | undefined;
    /** `xs` for dense rows, `sm` for headers. */
    size?: 'xs' | 'sm';
    className?: string;
}

export function BrandBadge({ brand, size = 'sm', className = '' }: BrandBadgeProps) {
    const { isMultiBrand } = useBrandProfile();
    // Outside the parent portal's provider there is one brand on offer and nothing to
    // distinguish, so a pill on every row would be noise.
    if (!isMultiBrand) return null;
    if (!isBrandKey(brand)) return null;
    const profile = BRAND_PROFILES[brand];

    return (
        <span
            className={`inline-flex items-center rounded-full border font-medium whitespace-nowrap ${
                size === 'xs' ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs'
            } ${className}`.trim()}
            style={{
                borderColor: profile.accent,
                backgroundColor: profile.accentSoft,
                color: profile.accentInk,
            }}
        >
            {profile.shortName}
        </span>
    );
}
